/**
 * Token-change notifications.
 *
 * Deliberately dependency-free. `constants/global.ts` calls `notifyTokenChanged` and
 * `authority.ts` reads `USERDATACOOKIE` from that same module — routing the notifier
 * through here keeps `global → token-store → authority → global` from becoming an
 * import cycle.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Call after any write to the stored auth token. Without it a same-tab token rotation
 * (`encodeData` after `POST /Auth/extend-session`) is invisible to React and every
 * permission gate keeps reading the previous claim set.
 */
export const notifyTokenChanged = () => {
	listeners.forEach((listener) => listener());
};

export const subscribeToTokenChange = (listener: Listener) => {
	listeners.add(listener);

	// Fires only for writes made by OTHER tabs — this is what makes logout propagate.
	const onStorage = () => listener();
	window.addEventListener('storage', onStorage);

	return () => {
		listeners.delete(listener);
		window.removeEventListener('storage', onStorage);
	};
};
